"use client";

import firebaseApp, { vapidKey } from "firebaseConfig";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useApiClient } from "~/api-client";
import {
  NotificationBanner,
  type NotificationDetails,
} from "~/components/NotificationBanner/NotificationBanner";
import { useLocations } from "./locations";
import { useUser } from "./user";

export function NotificationProvider(props: { children: React.ReactNode }) {
  const { user } = useUser();
  const apiClient = useApiClient();
  const router = useRouter();
  const pathname = usePathname();
  const { merchantName } = useLocations();

  // Has user already been prompted to allow notifications
  const [askedNotifications, setAskedNotifications] = useState(false);

  // In-app notification banner state
  const [notificationDetails, setNotificationDetails] =
    useState<null | NotificationDetails>(null);

  // Handle swift-wrapper push-token event
  const registerToken = useCallback(
    async (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        try {
          await apiClient.associateFCMToken(
            {},
            { fcmToken: customEvent.detail },
          );
        } catch (error) {
          console.error("Failed to associate FCM token:", error);
        }
      }
    },
    [apiClient],
  );

  // Handle swift-wrapper push-notification-received event
  const receiveNotification = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        const notification = customEvent.detail;
        setNotificationDetails({
          title: notification.title,
          body: notification.body,
        });
      }
    },
    [setNotificationDetails],
  );

  // Handle swift-wrapper push-notification-click event
  // web/android equivalent handled in firebase-messaging-sw
  const clickNotification = useCallback(() => {
    // Assume this is a fresh app start
    if (pathname === "/") {
      router.replace("/activity");
    }
  }, [router, pathname]);

  // Register push notifications when user logs in
  useEffect(() => {
    if (user === null) return;
    if (askedNotifications) return;

    (async () => {
      try {
        await registerPushNotifications(async (token) => {
          return apiClient.associateFCMToken({}, { fcmToken: token });
        });
        setAskedNotifications(true);
      } catch (e) {
        console.error("Failed to register push notifications:", e);
      }
    })();
  }, [apiClient, user, askedNotifications]);

  // Register for swift-wrapper events
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("push-token", registerToken);
    window.addEventListener("push-notification-received", receiveNotification);
    window.addEventListener("push-notification-click", clickNotification);
    return () => {
      window.removeEventListener("push-token", registerToken);
      window.removeEventListener(
        "push-notification-received",
        receiveNotification,
      );
      window.removeEventListener("push-notification-click", clickNotification);
    };
  }, [registerToken, receiveNotification, clickNotification]);

  // Shows an in-app banner in foreground; shows a native notification when the page isn't visible
  useEffect(() => {
    // Can't return a Promise<Unsubscribe> to useEffect, so have to capture the promise, and
    // return an unsubscribe lambda that calls the Unsubscribe from the promise
    const unsubPromise = (async () => {
      if (!(await isSupported())) return;

      const messaging = getMessaging(firebaseApp);
      return onMessage(messaging, (payload) => {
        if (!payload.notification) return;

        // Always update the in-app banner
        setNotificationDetails(payload.notification);

        // Only show native notification if page is in background/hidden
        if (
          typeof window !== "undefined" &&
          window.Notification &&
          Notification.permission === "granted" &&
          document.visibilityState !== "visible"
        ) {
          navigator.serviceWorker
            .getRegistrations()
            .then((registrations) => {
              if (registrations.length === 0) {
                return null;
              }

              const registration =
                registrations.find((reg) => reg.active) || registrations[0];

              if (!payload.notification) {
                return;
              }

              return registration.showNotification(
                payload.notification.title ?? merchantName ?? "",
                {
                  body: payload.notification.body,
                },
              );
            })
            .catch((error) => {
              console.error("Failed to show notification:", error);
            });
        }
      });
    })();
    return () => {
      unsubPromise.then((unsubCallback) => unsubCallback?.());
    };
  }, [setNotificationDetails, merchantName]);

  return (
    <>
      <NotificationBanner
        details={notificationDetails}
        onDismiss={() => setNotificationDetails(null)}
      />
      {props.children}
    </>
  );
}

export async function registerPushNotifications(
  associateToken: (token: string) => Promise<unknown>,
) {
  if (typeof window === "undefined") return;

  // Handler for push notifications via swift wrapper (iOS)
  if (
    window.webkit?.messageHandlers["push-permission-request"] &&
    window.webkit?.messageHandlers["push-token"]
  ) {
    // Check current permission state before prompting
    const permissionState = await checkIOSPermissionState();

    if (permissionState === "authorized") {
      // Already authorized - just get the token, skip prompt
      window.webkit.messageHandlers["push-token"].postMessage("push-token");
    } else if (permissionState === "denied") {
      // User previously denied - don't prompt again
      return;
    } else {
      // notDetermined or unknown - request permission
      window.webkit.messageHandlers["push-permission-request"].postMessage(
        "push-permission-request",
      );
      window.webkit.messageHandlers["push-token"].postMessage("push-token");
    }

    return;
  }

  // Native web push
  if (!(await isSupported())) {
    return;
  }

  // Guard: Check if Notification API is available
  if (typeof Notification === "undefined") {
    return;
  }

  // Short-circuit only if permission was denied
  if (Notification.permission === "denied") {
    return;
  }

  let permission: NotificationPermission = Notification.permission;

  // If permission is default, request it
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  // Proceed if permission is granted (either already or just granted)
  if (permission === "granted") {
    try {
      const messaging = getMessaging(firebaseApp);
      const token = await getToken(messaging, { vapidKey });

      // Defensively check token exists before associating
      if (token) {
        await associateToken(token);
      }
    } catch (error) {
      console.error(
        "Failed to get/associate FCM token:",
        error instanceof Error ? error.message : error,
      );
    }
  }
}

// Helper function to check iOS permission state
async function checkIOSPermissionState(): Promise<string> {
  return new Promise((resolve) => {
    const handleStateResponse = (event: Event) => {
      const customEvent = event as CustomEvent;
      const state = customEvent.detail || "unknown";
      window.removeEventListener("push-permission-state", handleStateResponse);
      resolve(state);
    };

    window.addEventListener("push-permission-state", handleStateResponse);

    if (window.webkit?.messageHandlers?.["push-permission-state"]) {
      window.webkit.messageHandlers["push-permission-state"].postMessage("");
    } else {
      window.removeEventListener("push-permission-state", handleStateResponse);
      resolve("unknown");
      return;
    }

    // Timeout after 1 second if iOS doesn't respond
    setTimeout(() => {
      window.removeEventListener("push-permission-state", handleStateResponse);
      resolve("notDetermined");
    }, 1000);
  });
}
